import {first, every, clone, isEmpty} from 'lodash-es';
import {computed, defineComponent, nextTick, onMounted, ref, useTemplateRef, watch} from 'vue';
import {IDrawZoneMapGeometry} from '@/app/shared/components/kl-draw-zone-map/kl-draw-zone-map';
import {useKlModalEventsUtil} from '@/app/shared/helpers/kl-modal-events-util';
import * as yup from 'yup';
import KlDrawZoneMap from '@/app/shared/components/kl-draw-zone-map/kl-draw-zone-map.vue';
import {useIsMobile} from '@/app/shared/composables/useIsMobile';
import {useMediaQuery} from '@vueuse/core/index';
import {
    EDrawZoneMapState
} from '@/app/shared/components/kl-draw-zone-map/components/kl-draw-zone-sidebar/kl-draw-zone-sidebar';

export interface ILocationPickerGeometry {
    coordinates: number[][][];
}

export default defineComponent({
    name: 'KlLocationpicker',
    emits: ['update:modelValue'],
    props: {
        modelValue: {
            type: Object as () => ILocationPickerGeometry,
            required: false,
            default: null,
        },
    },
    setup(props,  { emit }) {

        const mapComponent = useTemplateRef<InstanceType<typeof KlDrawZoneMap>>('mapComponentRef');

        const mapRules = yup.object()
            .kl_isPolygon();

        const location = ref<ILocationPickerGeometry>(null);
        const zones = ref<ILocationPickerGeometry[]>(null);
        const zonesValid = ref<boolean>(true);
        const drawZoneActive = ref<boolean>(false);

        const applyZoneButtonDisabled = computed(() => {
            return isEmpty(zones.value) || !zonesValid.value;
        });

        // should only be updated when the modal opens, not when it is already open!!
        // > kl-draw-zone-map doesn't handle changes to 'useSidebar' and 'useInputGeometriesForVisibleArea'
        const useVisibleArea = ref<boolean>(false);

        const onZonesChanged = (newZones: IDrawZoneMapGeometry[]) => {
            zones.value = newZones?.map((current: IDrawZoneMapGeometry) => ({ coordinates: current.coordinates as number[][][] }));
            zonesValid.value = every(newZones, (cz: IDrawZoneMapGeometry) => cz.isValid);
        }

        const editLocation = () => {
            // otherwise clicking pill.close would also open the modal
            if (location.value) {
                // init with current confirmed zone (ex. this will reset the changes after a cancel or unconfirmed close)
                _setZone(location.value);

                _toggleModal();
            }
        }

        const openModal = () => {
            _toggleModal();
        }

        const cancelModal = () => {
            _toggleModal();
        }

        const _toggleModal = () => {
            // only switch modes onOpen, not constantly
            useVisibleArea.value = _usePhoneSizeLayout();

            drawZoneActive.value = !drawZoneActive.value;
        }

        const _usePhoneSizeLayout = (): boolean => {
            // TO CHECK: kunnen we dit veralgemenen? of is dit te specifiek (deze control heeft een minimum width & height nodig)
            // useIsMobile() gebruikt momenteel enkel max-width, maar hier is dat niet voldoende (vb: landscape)
            return (useIsMobile().value || useMediaQuery('(max-height: 600px)').value);
        }

        const removeLocation = () => {
            drawZoneActive.value = false;
            location.value = null;
            zones.value = null;
            emit('update:modelValue', null);
        }

        const applyZone = async () => {
            location.value = first(zones.value);
            emit('update:modelValue', location.value);
            _toggleModal();
        }

        const searchOnVisibleArea = () => {
            location.value = {
                coordinates: mapComponent.value.getCurrentVisibleArea(),
            };
            emit('update:modelValue', location.value);
            _toggleModal();
        }

        const _setZone = (zone: ILocationPickerGeometry) => {
            if (zone) {
                location.value = zone;
                zones.value = [location.value];
            }
            else {
                location.value = null;
                zones.value = null;
            }
        }

        watch(
            () => props.modelValue,
            (newValue: ILocationPickerGeometry) => {
                _setZone(newValue);
            },
            {immediate: true, deep: true}
        )

        return {
            mapRules,
            location,
            zones,
            drawZoneActive,
            applyZoneButtonDisabled,
            onZonesChanged,
            removeLocation,
            applyZone,

            useVisibleArea,
            searchOnVisibleArea,

            editLocation,
            openModal,
            cancelModal,
        }
    }
})
